import { newIdempotencyKey } from "@anthra/platform/api"
import {
	formatIstDateTime,
	formatVettingId,
	hoursBetween,
	parseTimestamp,
	QUERY_CATEGORY_OPTIONS,
	type QueryCategory,
	queryCategoryLabel,
	type ResponseDraft,
	type VettingMessage,
	vettingMessageInputSchema,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import {
	describeTypes,
	formatBytes,
	type PlatformFile,
	QUERY_ATTACHMENT_RULES,
} from "@anthra/platform/files"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@anthra/ui/dialog"
import { ErrorState } from "@anthra/ui/feedback"
import { NativeSelect } from "@anthra/ui/field"
import { ExpandableRichText } from "@anthra/ui/rich-text"
import { ScrollFab } from "@anthra/ui/scroll-fab"
import { Skeleton, SkeletonRows, SkeletonText } from "@anthra/ui/skeleton"
import { TiptapEditor } from "@anthra/ui/tiptap-editor"
import { CardTitle, Description } from "@anthra/ui/typography"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronDownIcon } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"

import { AttachmentList } from "@/components/attachment-list"
import { FeedbackSheet } from "@/components/feedback-sheet"
import { NotFoundDenied } from "@/components/not-found-denied"
import { StatusBadge } from "@/components/status-badge"
import { UserWithOwnNumber } from "@/components/user-with-own-number"
import { copy } from "@/copy/expert-vetting-review"
import { useMarkPanelSeen } from "@/features/activity/use-panel-activity"
import {
	COMPOSER_ANCHOR_ID,
	isDraftConflict,
	markAnsweredBlockReason,
	scrollToComposer,
} from "@/features/drafts/draft-helpers"
import {
	DraftBlockedNote,
	DraftConflictNote,
	KeptDraftFiles,
	ResponseDraftCard,
} from "@/features/drafts/response-draft"
import { type DraftComposer, useDraftComposer } from "@/features/drafts/use-draft-composer"
import { DocumentPanes } from "@/features/vetting/document-panes"
import {
	useCloseVettingRequest,
	useExpertVettingRequest,
	useMarkVettingAsAnswered,
	useSaveVettingDraft,
	useSubmitVettingReview,
	useUpdateVettingCategory,
} from "@/features/vetting/use-expert-vetting"

/**
 * One vetting request, as an Expert reviews it (US-01-004.4/.5/.7/.8/.9/.12).
 *
 * OPENING IT IS A READ. Loading this screen changes nothing; the first message
 * an Expert posts moves an Open request to In Progress and records
 * `reviewedBy`. There is NO EXCLUSIVITY: another Expert may open the same
 * request and post a message.
 *
 * THE REVIEW IS A THREAD, mirroring a query's conversation: any Expert may
 * post any number of messages, and a second one is never refused (unlike the
 * old single-review rule this replaced). "Mark As Answered" stays a separate,
 * deliberate act — writing a message and telling the taxpayer are two
 * decisions, the same shape the query flow already has.
 *
 * The notice and the draft sit SIDE BY SIDE above the laptop breakpoint and become
 * tabs below it (US-01-004.4). Nothing on this screen edits the title, the category,
 * the notes or either upload — the API refuses those writes and no control exists
 * for them (US-01-004.12).
 *
 * Built against screens/expert-vetting-review.html: `.rec-head` for the record's
 * own header, `.layout`/`.pane` for the document comparison and `.composer` for
 * the message editor.
 */
export const Route = createFileRoute("/_panel/vetting/$vettingId")({
	component: VettingReview,
})

/**
 * A thread opens at its top, the question and the first messages, so the
 * conversation reads from the start (client feedback; it used to open at
 * its latest message — the composer is home.
 * Runs once the real content has painted, not the skeleton, so it scrolls to
 * the actual bottom rather than a placeholder's. Mirrors
 * `_panel.queries.$queryId.tsx`'s own hook exactly.
 */
function useScrollToLatestOnLoad(ready: boolean, key: string): void {
	useEffect(() => {
		if (!ready) return undefined
		// Deferred a tick: the router's own navigation-complete handler resets
		// scroll to the top AFTER this effect would otherwise run, undoing it.
		// Running one macrotask later lands after that reset.
		const timer = setTimeout(() => {
			if (window.location.hash === "#vetting-documents") {
				document.getElementById("vetting-documents")?.scrollIntoView({ block: "start" })
				return
			}
			window.scrollTo({ top: 0 })
		}, 0)
		return () => {
			clearTimeout(timer)
		}
	}, [ready, key])
}

function VettingReview() {
	const { vettingId } = Route.useParams()
	const request = useExpertVettingRequest(vettingId)
	useScrollToLatestOnLoad(request.isSuccess, vettingId)
	useMarkPanelSeen("vetting", vettingId, request.isSuccess)
	const markAsAnswered = useMarkVettingAsAnswered()
	// Set once, by MessageComposer's own onSuccess, and never cleared: it only
	// ever matters for the remainder of this mount, and the "Marked as
	// answered" callout is meant to persist for the rest of this visit
	// (US-01-004.9), against `.callout--success` on
	// screens/expert-vetting-review.html.
	const [markedAsAnswered, setMarkedAsAnswered] = useState(false)
	// Held here, not in the composer: the draft card in the thread loads into it.
	const composer = useDraftComposer()

	if (request.isPending) {
		// A REVIEW SCREEN, NOT EIGHT IDENTICAL BARS. The container carried the
		// same `h-4 w-full` as the eight children inside it, so a 16px box
		// clipped all eight and the loading state rendered as one grey line.
		// This is the shape that is actually coming: a heading, the two
		// document panes side by side, and the verdict form below.
		return (
			<div
				className="flex flex-col gap-6"
				role="status"
				aria-label="Loading the vetting request"
				aria-busy="true"
			>
				<SkeletonText lines={2} />
				<div className="grid gap-4 lg:grid-cols-2">
					<Skeleton className="h-64 w-full" />
					<Skeleton className="h-64 w-full" />
				</div>
				<SkeletonRows
					rows={3}
					cols={2}
				/>
			</div>
		)
	}

	if (request.isError) {
		const status = (request.error as { status?: number }).status
		if (status === 404) {
			return (
				<NotFoundDenied
					markTone="info"
					title="Request not found"
					lede="That vetting id does not exist."
					actions={
						<Link
							className={cn(buttonVariants({ variant: "primary" }))}
							to="/vetting"
						>
							Back to Vetting
						</Link>
					}
				/>
			)
		}
		return (
			<div
				data-screen="expert-vetting-review"
				className="flex flex-col gap-6"
			>
				<ErrorState
					error={request.error}
					onRetry={() => {
						void request.refetch()
					}}
				/>
			</div>
		)
	}

	const detail = request.data
	const submitted = parseTimestamp(detail.submittedAt)
	const canPostMessage = detail.status !== "closed"

	return (
		<div
			data-screen="expert-vetting-review"
			className="flex flex-col gap-6"
		>
			<ScrollFab />

			<Breadcrumb>
				<BreadcrumbItem>
					<Link
						to="/vetting"
						className="hover:text-foreground underline-offset-2 hover:underline"
					>
						Vetting
					</Link>
				</BreadcrumbItem>
				<BreadcrumbItem last>
					<BreadcrumbCurrent>{formatVettingId(detail.sequence)}</BreadcrumbCurrent>
				</BreadcrumbItem>
			</Breadcrumb>

			{markedAsAnswered ? (
				<div className="space-y-4">
					<div
						className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
						role="status"
					>
						<div>
							<h4 className="text-card-title text-foreground">{copy.success.hint}</h4>
							<div className="flex flex-wrap gap-2">
								<Link
									className={cn(buttonVariants({ variant: "primary" }))}
									to="/vetting"
								>
									{copy.default.btn3}
								</Link>
							</div>
						</div>
					</div>
				</div>
			) : null}

			{/* A RECORD HEADER, NOT FOUR THINGS IN A JUSTIFY-BETWEEN ROW.
			    The reference, the title, a <dl> of four facts and a paragraph
			    of policy were all flex children of one wrapping row set to
			    `justify-between`, so they were pushed to opposite ends of
			    whatever width was left and re-ordered themselves at every
			    breakpoint. The <dl> had no layout of its own either — bare
			    <dt>/<dd> pairs render as an indented stack, which is why the
			    facts read as a run-on list.

			    It stacks now: reference, then title with its status, then the
			    facts as a labelled grid, then the policy line last, where a
			    thing nobody needs on arrival belongs. */}
			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-field-label text-primary">Title</p>
					<h2 className="text-page-title text-heading min-w-0 break-all">
						{detail.title}
					</h2>
				</div>
				<dl className="flex flex-wrap gap-x-8 gap-y-3">
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Status of the request</dt>
						<dd>
							<StatusBadge
								status={detail.status}
								showDot
							/>
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Vetting Category</dt>
						<dd className="text-description text-foreground">
							{queryCategoryLabel(detail.category)}
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Submitted at</dt>
						<dd className="text-description text-foreground tabular-nums">
							{submitted ? formatIstDateTime(submitted) : "—"}
						</dd>
					</div>
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Submitted by</dt>
						<dd className="text-description text-foreground">
							<UserWithOwnNumber
								name={detail.userName}
								userReference={detail.userReference}
							/>
						</dd>
					</div>
					{detail.reviewedByName && detail.messages.length === 0 ? (
						<div className="flex flex-col gap-1">
							<dt className="text-meta text-muted">Opened by</dt>
							<dd className="text-description text-foreground">
								{detail.reviewedByName}
							</dd>
						</div>
					) : null}
				</dl>
			</div>

			{detail && (
				<CategoryControl
					vettingId={vettingId}
					category={detail.category}
					disabled={status === "closed"}
				/>
			)}
			{/* `text-muted` sets `color`, and an <hr> renders no text — the rule
			    kept its default colour. The border is what needs the token. */}
			<hr className="border-border" />

			{/* Side by side on a laptop, tabbed below it. `vettingId` is what
			    lets a pane open its file in the attachment viewer. */}
			<DocumentPanes
				notice={detail.notice}
				draft={detail.draft}
				vettingId={vettingId}
			/>

			{detail.notes ? (
				<div className="flex flex-col gap-3">
					<h2 className="text-card-title text-foreground text-primary">Description</h2>
					{/* Styled like the taxpayer app's own description block — same
					    left-accent callout query-detail uses for "The taxpayer's
					    question" — rather than a bordered card, so the two apps
					    read the taxpayer's own words the same way. */}
					<div className="border-border bg-surface-sunken border-l-accent rounded-card border border-l-[3px] p-6">
						<ExpandableRichText
							className="text-body text-foreground"
							html={detail.notes}
						/>
					</div>
				</div>
			) : null}

			<VettingMessageThread
				messages={detail.messages}
				userName={detail.userName}
			/>

			{/* Once Edit has moved the draft into the reply box, it is no longer
			    shown here: the text and files live in one place. */}
			{detail.responseDraft && canPostMessage && composer.loaded === null ? (
				<ResponseDraftCard
					draft={detail.responseDraft}
					onEdit={() => {
						if (!detail.responseDraft) return
						composer.load(detail.responseDraft)
						scrollToComposer()
					}}
				/>
			) : null}

			{/* User Feedback Section - Displayed when feedback is submitted */}
			<FeedbackSheet
				feedback={detail.feedback}
				userName={detail.userName}
			/>

			{canPostMessage ? (
				<MessageComposer
					composer={composer}
					draft={detail.responseDraft}
					vettingId={detail.id}
					status={detail.status}
					submittedAt={detail.submittedAt}
					hasExpertReply={detail.messages.some(
						message => message.senderType === "expert",
					)}
					markedAsAnswered={markedAsAnswered}
					onMarkedAsAnswered={() => {
						setMarkedAsAnswered(true)
					}}
					markAsAnsweredMutation={markAsAnswered}
				/>
			) : null}
		</div>
	)
}

/**
 * The message thread, matching the panel's own query thread
 * (`_panel.queries.$queryId.tsx`) exactly — same bare section (no card
 * chrome around the whole thread), same bubble shell, same
 * expert-on-the-right alignment, no day dividers, no per-message role label.
 */
function VettingMessageThread({
	messages,
	userName,
}: {
	messages: readonly VettingMessage[]
	userName: string
}) {
	return (
		<section
			className="flex min-w-0 flex-col gap-3"
			aria-label="Conversation"
		>
			<h2 className="text-card-title text-foreground text-primary">Conversation</h2>
			{messages.length === 0 ? (
				<p className="text-description text-subtle-foreground">No messages yet.</p>
			) : (
				<ol className="flex min-w-0 flex-col gap-6">
					{messages.map(message => {
						const isExpert = message.senderType === "expert"
						const created = parseTimestamp(message.createdAt)
						return (
							<li
								key={message.id}
								className={cn(
									"flex max-w-full gap-4",
									isExpert && "flex-row-reverse",
								)}
							>
								<div
									className={cn(
										"flex min-w-0 flex-1 flex-col gap-2",
										isExpert ? "items-end" : "items-start",
									)}
								>
									<div
										className={cn(
											"flex flex-wrap items-baseline gap-x-3 gap-y-2",
											isExpert && "justify-end",
										)}
									>
										<span className="text-field-label text-foreground font-medium">
											{isExpert
												? (message.senderName ?? "Swamithra")
												: userName}
										</span>
										<span className="text-meta text-muted tabular-nums">
											{created ? formatIstDateTime(created) : "—"}
										</span>
									</div>
									<div
										className={cn(
											"max-w-[70%] min-w-0 rounded-lg border px-3 py-1.5 shadow-sm",
											isExpert &&
												"bg-accent-soft border-accent-border rounded-tr-xs shadow-none",
											!isExpert && "bg-surface border-border rounded-tl-xs",
										)}
									>
										<ExpandableRichText
											className="text-body text-foreground"
											html={message.bodyRich}
										/>
									</div>
									{message.attachments.length > 0 ? (
										<div
											className={cn(
												"w-full min-w-0",
												isExpert && "flex justify-end",
											)}
										>
											<AttachmentList
												attachments={message.attachments}
												variant="thread"
											/>
										</div>
									) : null}
								</div>
							</li>
						)
					})}
				</ol>
			)}
		</section>
	)
}

function CategoryControl({
	vettingId,
	category,
	disabled,
}: {
	vettingId: string
	category: QueryCategory
	disabled: boolean
}) {
	const update = useUpdateVettingCategory()
	const selectId = useId()

	return (
		<section className="border-border bg-surface flex flex-col gap-4 rounded-lg border p-5 shadow-sm">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 className="text-card-title text-foreground">Vetting Category</h2>
			</div>
			{update.isSuccess ? (
				<div
					className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
					role="status"
				>
					<div className="flex min-w-0 flex-col gap-1">
						<span className="text-card-title text-foreground">Category corrected</span>
						<p>
							Now counted under {queryCategoryLabel(update.variables.category)} in
							reporting and under that filter in the queue.
						</p>
					</div>
				</div>
			) : null}
			<div className="flex max-w-sm min-w-56 flex-col gap-1.5">
				{/* Named by the card's heading above; the label stays for screen
				    readers only, so "Vetting Category" is shown once. */}
				<label
					className="sr-only"
					htmlFor={selectId}
				>
					Vetting Category
				</label>
				<span className="relative">
					<NativeSelect
						id={selectId}
						name="category"
						value={category}
						disabled={disabled || update.isPending}
						aria-invalid={update.isError}
						onChange={event => {
							if (disabled || update.isPending) return
							update.mutate({
								vettingId,
								category: event.target.value as QueryCategory,
							})
						}}
					>
						{QUERY_CATEGORY_OPTIONS.map(option => (
							<option
								key={option.value}
								value={option.value}
							>
								{option.label}
							</option>
						))}
					</NativeSelect>
					<ChevronDownIcon
						className="text-muted pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
						aria-hidden="true"
					/>
				</span>
				<p className="text-meta text-muted">
					{disabled
						? "Category cannot be changed on a closed vetting request."
						: "Correcting a mis-tagged request is recorded in the audit log."}
				</p>
				{update.isError ? (
					<p
						className="text-description text-subtle-foreground"
						role="alert"
					>
						{toDisplayable(update.error).message}
					</p>
				) : null}
			</div>
		</section>
	)
}

/**
 * The message composer (US-01-004.8/.9/.12), against `.composer`.
 *
 * An empty message is blocked inline by `vettingMessageInputSchema` before it
 * is sent. Unlike the old single-review rule, a second and later message is
 * never refused — the thread simply grows, and any Expert may add to it
 * (US-01-004.5 — no exclusivity). "Mark As Answered" shows while the request
 * is In Progress and is enabled once an Expert has replied at least once and
 * no saved draft is waiting: it is the Expert's own separate act of releasing
 * the thread to the taxpayer, the same two-step shape the query flow has.
 */
function MessageComposer({
	composer,
	draft,
	vettingId,
	status,
	submittedAt,
	hasExpertReply,
	markedAsAnswered,
	onMarkedAsAnswered,
	markAsAnsweredMutation,
}: {
	composer: DraftComposer
	/** The saved response draft on this request, or null. */
	draft: ResponseDraft | null
	vettingId: string
	status: "open" | "in_progress" | "answered" | "closed"
	submittedAt: string
	/** At least one Expert reply is in the thread. */
	hasExpertReply: boolean
	markedAsAnswered: boolean
	onMarkedAsAnswered: () => void
	markAsAnsweredMutation: ReturnType<typeof useMarkVettingAsAnswered>
}) {
	const submitReview = useSubmitVettingReview(() => {
		const submitted = parseTimestamp(submittedAt)
		return {
			hours_since_submitted: submitted ? Math.max(0, hoursBetween(submitted, new Date())) : 0,
		}
	})
	const save = useSaveVettingDraft()
	// A saved draft this Expert has not opened locks the composer: a second
	// message cannot be started beside it. Edit it first.
	const blocked = draft !== null && composer.loaded === null
	// Mark as Answered waits for at least one reply, and for a saved draft that
	// nobody has opened. Once Edit has loaded the draft here, it no longer blocks.
	const answerBlocked = markAnsweredBlockReason(blocked, hasExpertReply)
	const { bodyRich, files, rejections } = composer
	const [error, setError] = useState<string | undefined>(undefined)
	const sendKey = useRef<string | null>(null)
	const answerKey = useRef<string | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	/**
	 * Save draft. `over` is set when the Expert chose to save over a newer draft
	 * another Expert saved; see DraftConflictNote.
	 */
	function saveDraft(over?: ResponseDraft | null): void {
		setError(undefined)
		save.mutate(
			{ vettingId, input: composer.saveInput(over), attachments: files },
			{
				onSuccess: () => {
					composer.clear()
				},
			},
		)
	}

	function loadLatest(): void {
		if (!draft) return
		composer.load(draft)
		save.reset()
		submitReview.reset()
	}

	function send(): void {
		const parsed = vettingMessageInputSchema.safeParse({ bodyRich })
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message)
			return
		}
		setError(undefined)
		sendKey.current ??= newIdempotencyKey()

		submitReview.mutate(
			{
				vettingId,
				input: parsed.data,
				attachments: files,
				idempotencyKey: sendKey.current,
				fromDraft: composer.sendFromDraft(),
			},
			{
				onSuccess: () => {
					composer.clear()
					sendKey.current = null
				},
				onError: failure => {
					// A conflict changes what the retry will send (the Expert loads
					// the newer draft first), so it must not reuse this key.
					if (isDraftConflict(failure)) sendKey.current = null
				},
			},
		)
	}

	const closeRequest = useCloseVettingRequest()
	const closeKey = useRef<string | null>(null)
	function closeIt(): void {
		closeKey.current ??= newIdempotencyKey()
		closeRequest.mutate(
			{ vettingId, idempotencyKey: closeKey.current },
			{
				onSuccess: () => {
					// No WhatsApp message is sent on a close, by any route.
					closeKey.current = null
				},
			},
		)
	}

	function markAsAnswered(): void {
		answerKey.current ??= newIdempotencyKey()
		markAsAnsweredMutation.mutate(
			{ vettingId, idempotencyKey: answerKey.current },
			{
				onSuccess: () => {
					answerKey.current = null
					onMarkedAsAnswered()
				},
			},
		)
	}

	return (
		// `border-border bg-surface ... rounded-lg border p-5 shadow-sm` —
		// ResponseArea's own shell (_panel.queries.$queryId.tsx). The old
		// `composer`/`is-invalid` class names carried no styles at all: they
		// were the deleted screens/expert-vetting-review.css's own hooks, so
		// this card rendered with no border, no background and no padding.
		<details
			id={COMPOSER_ANCHOR_ID}
			className="group border-border bg-surface flex scroll-mt-6 flex-col gap-3 rounded-lg border p-5 shadow-sm"
		>
			{/* AN ACCORDION, CLOSED UNTIL ASKED FOR, like the user app's follow-up
			    box. The composer stays mounted while closed, so a half-written
			    reply is not lost by folding it away; Edit on a saved draft opens
			    it (scrollToComposer). */}
			<summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
				<h2 className="text-card-title text-foreground text-primary">Your message</h2>
				<svg
					className="text-muted size-5 shrink-0 transition-transform group-open:rotate-180"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="m6 9 6 6 6-6" />
				</svg>
			</summary>
			<div className="mt-2 flex flex-col gap-3">
				<p className="text-meta text-muted">
					Bold, italic, underline, bulleted and numbered lists and line breaks are kept.
				</p>

				{save.isSuccess && blocked ? (
					<div
						className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3"
						role="status"
					>
						<div className="flex min-w-0 flex-col gap-1">
							<span className="text-card-title text-foreground">Draft saved</span>
							<p>It is in the conversation above. Use Edit on it to carry on.</p>
						</div>
					</div>
				) : null}

				{blocked && !save.isSuccess ? <DraftBlockedNote draft={draft} /> : null}

				<TiptapEditor
					value={bodyRich}
					onChange={composer.setBodyRich}
					label="Write a message for the user"
					placeholder="Write your feedback on the draft."
					disabled={blocked || submitReview.isPending}
					error={error}
				/>

				<input
					ref={inputRef}
					type="file"
					className="sr-only"
					multiple
					accept={QUERY_ATTACHMENT_RULES.accepted.join(",")}
					aria-label="Attach files"
					onChange={event => {
						const chosen: PlatformFile[] = Array.from(event.target.files ?? []).map(
							file => ({
								name: file.name,
								type: file.type,
								sizeBytes: file.size,
								source: file,
							}),
						)
						if (chosen.length > 0) composer.addFiles(chosen)
						event.target.value = ""
					}}
				/>

				<div className="flex flex-wrap items-center justify-between">
					<div className="flex flex-row items-center gap-3">
						<button
							type="button"
							className={cn(buttonVariants({ variant: "secondary" }))}
							disabled={blocked}
							onClick={() => inputRef.current?.click()}
						>
							Attach files
						</button>
						<span className="text-meta text-muted tabular-nums">
							{describeTypes(QUERY_ATTACHMENT_RULES.accepted)} · up to{" "}
							{formatBytes(QUERY_ATTACHMENT_RULES.maxBytes)} ·{" "}
							{QUERY_ATTACHMENT_RULES.maxFiles} files maximum
						</span>
					</div>
					<div className="flex flex-row items-center gap-3">
						<Button
							variant="secondary"
							disabled={blocked || save.isPending || submitReview.isPending}
							onClick={() => {
								saveDraft()
							}}
						>
							{save.isPending ? "Saving…" : "Save draft"}
						</Button>
						<Button
							variant="primary"
							disabled={blocked || submitReview.isPending || save.isPending}
							onClick={send}
						>
							{submitReview.isPending ? "Sending…" : "Send message"}
						</Button>
						{status === "in_progress" && !markedAsAnswered ? (
							<Button
								variant="secondary"
								disabled={
									markAsAnsweredMutation.isPending || answerBlocked !== null
								}
								onClick={markAsAnswered}
							>
								{markAsAnsweredMutation.isPending
									? "Marking as answered…"
									: "Mark As Answered"}
							</Button>
						) : null}
						{/* After Mark as answered, Close follows, as in the query flow. */}
						{status === "answered" || (markedAsAnswered && status !== "closed") ? (
							<CloseRequestAction
								isPending={closeRequest.isPending}
								onConfirm={closeIt}
							/>
						) : null}
					</div>
				</div>

				{status === "in_progress" && !markedAsAnswered && answerBlocked ? (
					<p className="text-meta text-muted">{answerBlocked}</p>
				) : null}

				<KeptDraftFiles
					files={composer.loaded?.kept ?? []}
					onRemove={composer.removeKept}
				/>

				{files.length > 0 ? (
					<ul className="flex flex-col gap-2">
						{files.map((file, index) => (
							<li
								key={`${file.name}-${String(index)}`}
								className="border-border-subtle bg-surface-sunken flex items-center gap-3 rounded-md border px-3 py-2"
							>
								<span className="text-description text-foreground min-w-0 flex-1 truncate">
									{file.name}
								</span>
								<span className="text-meta text-muted shrink-0 tabular-nums">
									{formatBytes(file.sizeBytes)}
								</span>
								<Button
									type="button"
									variant="quiet"
									size="icon"
									aria-label={`Remove ${file.name}`}
									onClick={() => {
										composer.removeFile(index)
									}}
								>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.7"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<path d="M6 6l12 12M18 6 6 18" />
									</svg>
								</Button>
							</li>
						))}
					</ul>
				) : null}

				{rejections.length > 0 ? (
					<ul className="flex flex-col gap-2">
						{rejections.map(rejection => (
							<li
								key={rejection.message}
								className="border-danger-border bg-danger-soft flex items-center gap-2 rounded-md border px-3 py-2"
								role="alert"
							>
								<span className="text-description text-danger min-w-0 flex-1 truncate">
									{rejection.message}
								</span>
							</li>
						))}
					</ul>
				) : null}

				{/* ErrorState draws its own titled, breach-coloured card. Wrapping it in
			    a second one put a card inside a card and the word "error" on the
			    screen twice — the defect _panel.vetting.index.tsx says was fixed on
			    the sibling screen and which never reached this one. */}
				{save.isError && isDraftConflict(save.error) ? (
					<DraftConflictNote
						error={save.error}
						latest={draft}
						onLoadLatest={loadLatest}
						onSaveOver={() => {
							saveDraft(draft)
						}}
					/>
				) : null}
				{save.isError && !isDraftConflict(save.error) ? (
					<ErrorState
						error={save.error}
						onRetry={() => {
							saveDraft()
						}}
					/>
				) : null}
				{submitReview.isError && isDraftConflict(submitReview.error) ? (
					<DraftConflictNote
						error={submitReview.error}
						latest={draft}
						onLoadLatest={loadLatest}
					/>
				) : null}
				{submitReview.isError && !isDraftConflict(submitReview.error) ? (
					<ErrorState
						error={submitReview.error}
						onRetry={send}
					/>
				) : null}
				{closeRequest.isError ? <ErrorState error={closeRequest.error} /> : null}
				{markAsAnsweredMutation.isError ? (
					<ErrorState error={markAsAnsweredMutation.error} />
				) : null}

				<div className="border-border flex flex-wrap items-center justify-between gap-2 border-t pt-3">
					<p className="text-meta text-muted">
						Sending posts to the user&apos;s thread. Every message stays on the record —
						nothing here can be edited once sent.
					</p>
					<div className="flex flex-wrap gap-2">
						<Link
							className={cn(buttonVariants({ variant: "ghost" }))}
							to="/vetting"
						>
							{copy.default.btn3}
						</Link>
					</div>
				</div>
			</div>
		</details>
	)
}

/**
 * 'Close Request', and the confirmation it goes through: the vetting twin of
 * the query screen's 'Close Query'. Closing is one-way (the user loses the
 * follow-up box and nothing reopens it), so it takes a second, deliberate
 * click, and the dialog says what closing does.
 */
function CloseRequestAction({
	isPending,
	onConfirm,
}: {
	isPending: boolean
	onConfirm: () => void
}) {
	const [open, setOpen] = useState(false)

	return (
		<>
			<Button
				variant="secondary"
				disabled={isPending}
				onClick={() => {
					setOpen(true)
				}}
			>
				Close Request
			</Button>

			<Dialog
				open={open}
				onOpenChange={setOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle asChild>
							<CardTitle as="h2">Close this vetting request?</CardTitle>
						</DialogTitle>
						<DialogDescription asChild>
							<Description>
								The user keeps the thread but loses the follow-up box. Closing
								cannot be undone, and they are not notified.
							</Description>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="secondary"
							onClick={() => {
								setOpen(false)
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={isPending}
							onClick={() => {
								setOpen(false)
								onConfirm()
							}}
						>
							{isPending ? "Closing…" : "Yes, close it"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
